import { useCallback, useRef } from "react";

/**
 * Tracks whether any field of a form-state object differs from a captured
 * snapshot. Mirrors `snapshotForm` / `isDirty` in the legacy
 * `static/new-entry.js` — selects count as "dirty" only when their current
 * value differs from the initial selection.
 *
 * Usage:
 *   const { snapshot, isDirty } = useDirtyForm<FormShape>();
 *   useEffect(() => snapshot(initial), [initial]); // capture pristine state
 *   ...
 *   if (isDirty(currentValues)) confirm("Discard?");
 */
export function useDirtyForm<T extends Record<string, string>>() {
  const snapshotRef = useRef<T | null>(null);

  const snapshot = useCallback((values: T) => {
    // Shallow clone — the values themselves are primitives.
    snapshotRef.current = { ...values };
  }, []);

  const isDirty = useCallback((current: T) => {
    const initial = snapshotRef.current;
    if (!initial) return false;
    for (const key of Object.keys(current) as Array<keyof T>) {
      if (current[key] !== (initial[key] ?? "")) return true;
    }
    return false;
  }, []);

  return { snapshot, isDirty };
}
